'use client'

import { Box, Container, Grid, GridItem } from '@chakra-ui/react'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import Slider from '@/_components/Slider'
import Breadcrumbs from '@/_components/Breadcrumbs'
import { useTranslations } from 'next-intl'
import { useCustomLocale } from '@/_hooks/useCustomLocale'
import { IRecipe } from '@/_types'
import Header from '@/app/[locale]/(home)/_components/Header'
import dayjs from 'dayjs'
import RecipeDetails from '../RecipeDetails'
import RecipeSteps from '../RecipeSteps'
import type { RecipeReviewsPayload } from '../types'
import { getPublicSeoOrigin } from '@/i18n/urls'

export default function Desktop({
  data,
  reviews,
}: {
  data: IRecipe
  reviews?: RecipeReviewsPayload
}) {
  const t = useTranslations()
  const currentLocale = useCustomLocale()
  const ref = useRef<HTMLDivElement>(null)
  const [maxImgH, setMaxImgH] = useState('600px')

  const breadCrumbs = useMemo(
    () => [
      { title: t('home'), link: '/' },
      { title: t('recipes'), link: '/recipes' },
      { title: data?.title?.[currentLocale] || '' },
    ],
    [t, data?.title, currentLocale]
  )

  const domain = getPublicSeoOrigin()

  const breadcrumbJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: breadCrumbs.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.title,
      item: item.link ? `${domain}${item.link}` : undefined,
    })),
  }

  const recipeJsonLd = data
    ? {
        '@context': 'https://schema.org',
        '@type': 'Recipe',
        author: {
          '@type': 'Person',
          name: data?.author,
        },
        cookTime: `PT${data?.cook_time || 0}M`,
        prepTime: `PT${data?.cook_time || 0}M`,
        datePublished: dayjs(data?.created_at).format('YYYY-MM-DD'),
        dateModified: dayjs(data?.updated_at).format('YYYY-MM-DD'),
        description: String(data?.description?.[currentLocale] || '').replace(
          /<[^>]*>/g,
          ''
        ),
        image: data?.images?.[0]
          ? process.env.BASE_URL + data.images[0]
          : '',
        recipeIngredient: data?.ingredients?.map(
          (item) => item?.[currentLocale]
        ),
        recipeInstructions: data?.steps?.map((el) => ({
          '@type': 'HowToStep',
          text: el?.[currentLocale],
        })),
        nutrition: {
          '@type': 'NutritionInformation',
          calories: data?.energy_from,
          fatContent: data?.fats,
        },
        name: data?.title?.[currentLocale],
        recipeYield: data?.portion,
      }
    : null

  useEffect(() => {
    const el = ref.current
    if (!el) return

    const resizeObs = new ResizeObserver(([entry]) => {
      const w = entry.contentRect.width
      setMaxImgH(`${Math.min(w * (2 / 3), 600)}px`)
    })
    resizeObs.observe(el)
    return () => resizeObs.disconnect()
  }, [data?.images])

  return (
    <Box bg={'background'} minH={'100dvh'} pb={10}>
      <Header />
      <Container maxW={'1200px'} w='90%' mb={20} p={0}>
        <Breadcrumbs data={breadCrumbs} />
        <Grid templateColumns='1fr 1fr' gap={10} mt={4}>
          <GridItem>
            <Box ref={ref} rounded={16} overflow='hidden'>
              {data?.images?.length > 0 && (
                <Slider
                  list={data.images}
                  maxHeight={maxImgH}
                  aspectRatio={3 / 2}
                  additionalStyles={{ borderRadius: '16px' }}
                />
              )}
            </Box>
            <Box mt={8}>
              <RecipeSteps data={data} />
            </Box>
          </GridItem>
          <GridItem>
            <RecipeDetails data={data} reviews={reviews} />
          </GridItem>
        </Grid>
      </Container>

      <script
        type='application/ld+json'
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbJsonLd) }}
      />
      {recipeJsonLd && (
        <script
          type='application/ld+json'
          dangerouslySetInnerHTML={{ __html: JSON.stringify(recipeJsonLd) }}
        />
      )}
    </Box>
  )
}
